import React, { useEffect, useState } from 'react';
import Icon, { SvgSrc } from '~/components/shared/Icon/Icon';
import { Typography } from '@qred/components-library';
import { ComponentSize } from '~/styles/BaseStyledComponentsOld';
import { theme } from '~/styles/themes';
import useTranslate from '~/hooks/useTranslate';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { MenuItem } from '~/Interfaces';
import { Language } from '~/store/types/sharedTypes';
import { RootState } from '~/store';
import { setLanguage } from '~/store/slices/intl.slice';
import { logToSentry } from '~/helpers/loggers.helper';
import { getMarketLanguageLabels } from '~/constants/markets';
import useLogout from '~/hooks/useLogout';
import { openHelpPopup } from '~/store/slices/helpPopup.slice';
import {
  LanguageContainer,
  LanguageOption,
  MenuItemContainer,
  MenuWrapper,
  TransitionedUnderline,
} from './StyledComponentsMenu';
import { usePostHog } from 'posthog-js/react';

interface Props {
  onMenuItemClick: () => void;
}

const MenuItems: React.FC<Props> = ({ onMenuItemClick }) => {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const { intl } = useSelector((state: RootState) => state);
  const { language, market } = intl;
  const logout = useLogout();
  const { isAuthenticated } = useAuth0();
  const [logsHaveBeenSent, setLogsHaveBeenSent] = useState(false);
  const postHog = usePostHog();

  useEffect(() => {
    postHog.capture('menu_open');

    return () => {
      postHog.capture('menu_close');
    };
  }, []);

  const onHelpClicked = () => {
    dispatch(openHelpPopup());
    postHog.capture('help_button_pressed');
  };

  const handleLanguageChanged = (newLanguage: Language) => {
    dispatch(setLanguage(newLanguage));
  };

  const onLogoutClicked = () => {
    postHog.capture('logout_button_pressed');

    logout();
  };

  const sendLogs = () => {
    if (!logsHaveBeenSent) {
      logToSentry(
        Error('Error manually sent from menu'),
        'Manually triggered error'
      );
      setLogsHaveBeenSent(true);
      setTimeout(() => {
        setLogsHaveBeenSent(false);
      }, 5000);
    }
  };

  const MenuItemOptions: Array<MenuItem> = [
    {
      label: translate('Help') as string,
      icon: SvgSrc.Comments,
      onClick: onHelpClicked,
    },
    {
      label: translate('Onboarding.Exit') as string,
      icon: SvgSrc.SignOut,
      onClick: onLogoutClicked,
      hide: !isAuthenticated,
    },
    {
      label: logsHaveBeenSent ? 'Thank you!' : 'Send logs',
      icon: logsHaveBeenSent ? SvgSrc.CheckCircle : SvgSrc.Rocket,
      onClick: sendLogs,
      hide: import.meta.env.MODE !== 'internal-prod',
    },
  ];

  const marketLanguages = getMarketLanguageLabels(market);

  return (
    <>
      <MenuWrapper onClick={onMenuItemClick}>
        {MenuItemOptions.filter((item) => !item.hide).map((item: MenuItem) => (
          <MenuItemContainer key={item.label} onClick={() => item.onClick()}>
            <TransitionedUnderline>
              <Icon
                mr={0.5}
                src={item.icon}
                color={
                  item.active ? theme.colors.dark : theme.colors.secondaryGray
                }
                size={ComponentSize.MenuIcon}
              />
              <Typography weight={item.active ? 600 : undefined}>
                {item.label}
              </Typography>
            </TransitionedUnderline>
          </MenuItemContainer>
        ))}
        {marketLanguages?.length > 1 && (
          <LanguageContainer>
            {marketLanguages.map((languageOption) => (
              <LanguageOption
                key={languageOption.value}
                active={languageOption.value === language}
                onClick={() => handleLanguageChanged(languageOption.value)}
              >
                {languageOption.label.short}
              </LanguageOption>
            ))}
          </LanguageContainer>
        )}
      </MenuWrapper>
    </>
  );
};

export default MenuItems;
